import { useEffect, useRef } from 'react'
import L from 'leaflet'
import type { Position } from '../../../../types'

interface UseUserMarkerProps {
  mapRef: React.MutableRefObject<L.Map | null>
  poiOverlayRef: React.MutableRefObject<HTMLDivElement | null>
  isInitializedRef: React.MutableRefObject<boolean>
  position: Position | null
  accuracy?: number | null
  updateOverlayPositions: () => void
  userMarkerElementRef: React.MutableRefObject<HTMLDivElement | null>
}

export const useUserMarker = ({
  mapRef,
  poiOverlayRef,
  isInitializedRef,
  position,
  accuracy,
  updateOverlayPositions,
  userMarkerElementRef,
}: UseUserMarkerProps) => {
  const accuracyCircleRef = useRef<L.Circle | null>(null)

  useEffect(() => {
    if (!poiOverlayRef.current || !mapRef.current || !isInitializedRef.current) return

    try {
      const container = mapRef.current.getContainer()
      if (!container || !container.offsetParent || container.offsetWidth === 0) {
        return
      }
    } catch (error) {
      return
    }

    if (!position) {
      if (userMarkerElementRef.current) {
        userMarkerElementRef.current.remove()
        userMarkerElementRef.current = null
      }
      if (accuracyCircleRef.current) {
        try {
          mapRef.current.removeLayer(accuracyCircleRef.current)
        } catch (error) {
          // Ignore
        }
        accuracyCircleRef.current = null
      }
      return
    }

    const { lat, lon } = position
    const ll: [number, number] = [lat, lon]

    // Create or update user marker element
    if (!userMarkerElementRef.current) {
      const userElement = document.createElement('div')
      userElement.className = 'user-marker-overlay'
      
      // Calculate initial position immediately
      const initialPoint = mapRef.current.latLngToContainerPoint([lat, lon])
      
      userElement.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 16px;
        height: 16px;
        margin-left: -8px;
        margin-top: -8px;
        border-radius: 50%;
        background-color: #5aa7ff;
        border: 3px solid #fff;
        box-shadow: 0 2px 6px rgba(90, 167, 255, 0.6);
        z-index: 950;
        pointer-events: none;
        transform: translate(${initialPoint.x}px, ${initialPoint.y}px);
        will-change: transform;
      `
      poiOverlayRef.current?.appendChild(userElement)
      userMarkerElementRef.current = userElement
    }

    updateOverlayPositions()

    // Update accuracy circle
    if (accuracy != null && accuracy > 0) {
      if (!accuracyCircleRef.current) {
        try {
          accuracyCircleRef.current = L.circle(ll, {
            radius: accuracy,
            color: '#5aa7ff',
            opacity: 0.3,
            fillOpacity: 0.1,
            weight: 2,
          }).addTo(mapRef.current)
        } catch (error) {
          console.warn('Error adding accuracy circle:', error)
        }
      } else {
        try {
          accuracyCircleRef.current.setLatLng(ll)
          accuracyCircleRef.current.setRadius(accuracy)
        } catch (error) {
          console.warn('Error updating accuracy circle:', error)
        }
      }
    } else if (accuracyCircleRef.current) {
      try {
        mapRef.current.removeLayer(accuracyCircleRef.current)
        accuracyCircleRef.current = null
      } catch (error) {
        // Ignore
      }
    }
  }, [position, accuracy, mapRef, poiOverlayRef, isInitializedRef, updateOverlayPositions, userMarkerElementRef])
}
